// A timeline row's linked artifacts as chips (lane B10, slice S18; plan D12; BRD 4.3 "Show actor, date/time, source
// and linked artifacts subject to permission."). The server already resolved each link for this reader
// (app/reporting/artifacts.py): a chip the reader could not open is not in the row at all, never greyed, never counted.
// A link carries a type, an id, a route or the tool that opens it and a version; never an amount, a candidate's name or
// free text, so the chip says only his word for the record and its version.
//
// `artifactChips` gives the D0 History its row's artifacts (DC-20: the chips deep-link, the only focus stops in a
// row); `ArtifactChips` draws the same chips on their own, for a host that shows a row's links outside the list.

import type { ArtifactLink } from "@/api/tools";
import { Chip, Chips, TextLink, VERSION_TEMPLATES, type HistoryArtifact } from "@/mc";
import { ARTIFACT_LABELS, TIMELINE_LABELS } from "./labels";
import { SEPARATOR, ownEntry } from "./sentences";
import { hsKey } from "@/views/b10/lib/hsKey";

/** The hotspot prefix of every timeline control (plan D17). */
export const TL_HS = "tl";

const FILE_DOOR = "file.download";

/** His word for an artifact type; a type he has not named yet reads "Linked artifact". */
export function artifactWord(type: string): string {
  return ownEntry(ARTIFACT_LABELS as Readonly<Record<string, string>>, type) ?? TIMELINE_LABELS.linkedArtifact;
}

/**
 * Where a chip goes: the screen route the server gave, else the file door for a file the reader may download (a
 * résumé or job description version), else nowhere (the chip is words only).
 */
export function artifactHref(link: Pick<ArtifactLink, "id" | "route" | "open_tool">): string | undefined {
  if (link.route) return link.route;
  if (link.open_tool === FILE_DOOR) return `/api/files/${encodeURIComponent(link.id)}`;
  return undefined;
}

/** The chip's words: his word for the record, and its version when it has one ("Resume version · v2"). */
export function artifactLabel(link: Pick<ArtifactLink, "type" | "version">): string {
  const word = artifactWord(link.type);
  return link.version === null || link.version === undefined ? word : `${word}${SEPARATOR}${VERSION_TEMPLATES.version({ n: link.version })}`;
}

/** A row's links as D0 History artifacts, in the row's order, each with its `tl.<type>` hotspot. */
export function artifactChips(links: readonly ArtifactLink[] | null | undefined, hsPrefix: string = TL_HS): HistoryArtifact[] {
  return (links ?? []).map((link) => ({ label: artifactLabel(link), href: artifactHref(link), hs: hsKey(hsPrefix, link.type) }));
}

export interface ArtifactChipsProps {
  links: readonly ArtifactLink[] | null | undefined;
  hsPrefix?: string | undefined;
  /** The group's name, drawn before the chips (default none). */
  label?: string | undefined;
}

/** The same chips outside a History row: words only where nothing opens, a link where something does. */
export function ArtifactChips({ links, hsPrefix = TL_HS, label }: ArtifactChipsProps) {
  const chips = artifactChips(links, hsPrefix);
  if (chips.length === 0) return null;
  return (
    <Chips label={label}>
      {chips.map((chip, index) => (
        <Chip key={`${chip.label}-${index}`} sm hs={chip.href === undefined ? chip.hs : undefined}>
          {chip.href === undefined ? chip.label : <TextLink href={chip.href} hs={chip.hs}>{chip.label}</TextLink>}
        </Chip>
      ))}
    </Chips>
  );
}
